"use client";

import dynamic from "next/dynamic";
import { useState, type FocusEvent, type PointerEvent } from "react";

import {
  RASHI_CHECK_FIELD_IDS,
  RASHI_CHECK_FORM,
  RASHI_CHECK_FRAME,
} from "@/components/rashi/rashi-check-form";
import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";

import { SHANI_MOON_CHECK_RESERVE } from "./shani-moon-check-copy";

// The /shani-transit/ check's one boundary, the transits hubs' pattern
// (TransitsMoonCheckLazy, copied rather than imported so this route owns every
// file it renders): ShaniMoonCheckSection imports only this module, and it
// owns the one dynamic() to the island. The form, place search, kundali
// client, verdict, answer and analytics load with the island; the first load
// gains only this file, the shell and the form props.
//
// Activation is element-scoped and local: no window listener and no timer, so
// a reader scrolling the page never downloads the island and the deploy's
// body pointerdown smoke loads nothing; a press, focus or submit on the shell
// does.
//
// The slot reserves the tallest state and every state fills it, so neither
// shell -> form nor a slow engine reply -> answer (or error) moves the page
// below. The form is the rashi check's (RASHI_CHECK_FORM, unchanged), so its
// tallest state is the one the transits hubs measured on 2026-10-15: the
// engine's place error after a typed place nobody picked, 560 px at 320-390
// wide and 452 from 640. This page's own states, measured 2026-10-20 (next
// dev + Playwright on /shani-transit/, POST /kundali and transits/current
// stubbed, 320, 360, 390, 412, 640 and 1280 px): the shell and form are 436
// below 412 px wide, 420 at 412 and 420 / 362 from 640; an engine failure
// keeps the form's height; the tallest answer is the exact sign with its edge
// note, 502 at 320, 460 at 360-390 and 412 from 640; the day's two windows
// with a "Show Shani for" button each reach 461 at 320 and 382 from 640.
// Hence 600 px on phones and 500 px from the sm breakpoint, as on the hubs.
// With them the slot's bottom never moved and no layout shift counted in any
// outcome, the picker selection above included.
// translate="no" covers the shell, form and answer, which re-render on "check
// another" (tests/translate-safe-islands.test.ts).

const SHELL = { ...RASHI_CHECK_FORM, className: RASHI_CHECK_FRAME };

const ShaniMoonCheck = dynamic(() => import("./ShaniMoonCheck"), {
  ssr: false,
  loading: () => <BirthToolFormShell {...SHELL} />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const SHELL_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: RASHI_CHECK_FIELD_IDS.date,
  tob: RASHI_CHECK_FIELD_IDS.time,
  place: RASHI_CHECK_FIELD_IDS.place,
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && SHELL_FIELD_IDS[name]) || RASHI_CHECK_FIELD_IDS.date;
}

export default function ShaniMoonCheckLazy() {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);

  // The shell's own onActivate cannot tell a keyboard focus from a touch, so
  // the slot reads the intent in the capture phase: focus follows the visitor
  // into the real form after a keyboard focus, a mouse press or a submit, and
  // never after a touch, which may be the start of a scroll.
  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(RASHI_CHECK_FIELD_IDS.date),
      };

  return (
    <div
      translate="no"
      data-shani-moon-check-slot=""
      className={`flex flex-col ${SHANI_MOON_CHECK_RESERVE}`}
      {...shellIntent}
    >
      {active ? (
        <ShaniMoonCheck focusFieldId={focusFieldId} />
      ) : (
        <BirthToolFormShell {...SHELL} onActivate={() => setActive(true)} />
      )}
    </div>
  );
}
